import { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, X, Loader2, ArrowLeft, ArrowRight, Link2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

export async function uploadFile(file) {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
  return data;
}

export default function ImageUploader({ images, onChange, single = false, compact = false, testId = "image-uploader" }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(0);
  const [urlInput, setUrlInput] = useState("");

  const uploadFiles = async (files) => {
    let list = Array.from(files || []).filter((f) => f.type.startsWith("image/"));
    if (single) list = list.slice(0, 1);
    if (!list.length) return;
    setUploading(list.length);
    const added = [];
    for (const file of list) {
      try {
        const data = await uploadFile(file);
        added.push(data.url);
      } catch (err) {
        toast.error(`${file.name}: ${formatApiError(err)}`);
      } finally {
        setUploading((n) => n - 1);
      }
    }
    if (added.length) {
      onChange(single ? added : [...images, ...added]);
      toast.success(`${added.length} image${added.length > 1 ? "s" : ""} uploaded.`);
    }
  };

  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= images.length) return;
    const next = [...images];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };

  const addUrl = () => {
    const u = urlInput.trim();
    if (!u) return;
    onChange(single ? [u] : [...images, u]);
    setUrlInput("");
  };

  const pad = compact ? "px-3 py-4" : "px-4 py-7";

  return (
    <div data-testid={testId}>
      <div
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); uploadFiles(e.dataTransfer.files); }}
        onClick={() => inputRef.current?.click()}
        data-testid={`${testId}-dropzone`}
        className={`flex cursor-pointer flex-col items-center justify-center gap-1.5 border border-dashed border-forest/30 bg-white text-center transition-colors hover:border-gold ${pad}`}
      >
        {uploading > 0 ? <Loader2 className="h-5 w-5 animate-spin text-gold-dark" /> : <Upload className="h-5 w-5 text-gold-dark" />}
        <p className="text-sm text-forest">{uploading > 0 ? `Uploading ${uploading} image${uploading > 1 ? "s" : ""}...` : single ? "Click or drag a photo here" : "Click or drag product photos here"}</p>
        <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-forest/45">JPG, PNG, WEBP · max 8 MB{single ? "" : " each"}</p>
        <input ref={inputRef} type="file" accept="image/*" multiple={!single} className="hidden" data-testid={`${testId}-file-input`} onChange={(e) => { uploadFiles(e.target.files); e.target.value = ""; }} />
      </div>

      {images.length > 0 && (
        <div className={`mt-3 grid gap-3 ${single ? "grid-cols-2 sm:grid-cols-3" : "grid-cols-3 sm:grid-cols-4"}`} data-testid={`${testId}-thumbnails`}>
          {images.map((src, i) => (
            <div key={src + i} className={`group relative overflow-hidden border border-forest/10 bg-white ${single ? "aspect-video" : "aspect-square"}`} data-testid={`${testId}-thumb-${i}`}>
              <img src={src} alt={`Image ${i + 1}`} className="h-full w-full object-cover" />
              {!single && i === 0 && <span className="absolute left-1.5 top-1.5 bg-gold px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-[0.14em] text-forest">Cover</span>}
              <div className="absolute inset-x-0 bottom-0 flex items-center justify-between bg-forest-deep/80 px-1.5 py-1 opacity-0 transition-opacity group-hover:opacity-100">
                {!single ? <button type="button" onClick={() => move(i, -1)} aria-label="Move left" data-testid={`${testId}-move-left-${i}`} className="p-1 text-parchment/80 hover:text-gold-light disabled:opacity-30" disabled={i === 0}><ArrowLeft className="h-3.5 w-3.5" /></button> : <span />}
                <button type="button" onClick={() => onChange(images.filter((_, j) => j !== i))} aria-label="Remove image" data-testid={`${testId}-remove-${i}`} className="p-1 text-parchment/80 hover:text-terracotta"><X className="h-3.5 w-3.5" /></button>
                {!single ? <button type="button" onClick={() => move(i, 1)} aria-label="Move right" data-testid={`${testId}-move-right-${i}`} className="p-1 text-parchment/80 hover:text-gold-light disabled:opacity-30" disabled={i === images.length - 1}><ArrowRight className="h-3.5 w-3.5" /></button> : <span />}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-3 flex gap-2">
        <div className="relative flex-1">
          <Link2 className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-forest/40" />
          <input value={urlInput} onChange={(e) => setUrlInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addUrl(); } }} placeholder="Or paste an image URL" data-testid={`${testId}-url-input`} className="w-full border border-forest/15 bg-white py-2 pl-9 pr-3 text-xs text-forest focus:border-gold focus:outline-none" />
        </div>
        <button type="button" onClick={addUrl} data-testid={`${testId}-url-add`} className="border border-forest/20 px-4 text-[10px] font-semibold uppercase tracking-[0.14em] text-forest hover:border-gold transition-colors">{single ? "Use" : "Add"}</button>
      </div>
    </div>
  );
}

export function SingleImageField({ label, value, onChange, testId }) {
  return (
    <div>
      <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-forest/55 mb-1.5">{label}</label>
      <ImageUploader single compact testId={testId} images={value ? [value] : []} onChange={(imgs) => onChange(imgs[0] || "")} />
    </div>
  );
}
